/**
 * cachedFetch — small in-session HTTP response cache.
 *
 * Used by PortfolioOverview / PortfolioPerformance / CommercialOverview to
 * skip the network round-trip when the same (URL + params) was loaded
 * within the TTL window. Pairs with the backend atlas cache:
 *   • backend cache  — saves BQ slot time across users/sessions
 *   • this cache     — saves the entire network roundtrip within one tab
 *
 * Together they make period-picker flipping back-and-forth feel instant.
 *
 * Returns the cached body directly (not a fake axios response) so the
 * caller can treat it the same way it treats `r.data` from a real fetch.
 *
 * Default TTL: 5 minutes. Cache is per-module so the same key is shared
 * across all pages that import this util — a `/overview` payload fetched
 * by PortfolioOverview is still warm when the user navigates to
 * CommercialOverview a second later.
 */

import axios from 'axios';

const CACHE      = new Map();
const TTL_MS     = 5 * 60 * 1000;
const MAX_SIZE   = 48;

function makeKey(url, params) {
  const sorted = params
    ? Object.keys(params)
        .filter((k) => params[k] !== undefined && params[k] !== null)
        .sort()
        .map((k) => `${k}=${params[k]}`)
        .join('&')
    : '';
  return `${url}?${sorted}`;
}

export function cacheGet(url, params) {
  const key = makeKey(url, params);
  const e = CACHE.get(key);
  if (!e) return null;
  if (Date.now() - e.t > TTL_MS) {
    CACHE.delete(key);
    return null;
  }
  // Move to "most recent" by re-inserting (Map preserves insertion order).
  CACHE.delete(key);
  CACHE.set(key, e);
  return e.data;
}

export function cacheSet(url, params, data) {
  const key = makeKey(url, params);
  if (CACHE.size >= MAX_SIZE && !CACHE.has(key)) {
    CACHE.delete(CACHE.keys().next().value);
  }
  CACHE.set(key, { data, t: Date.now() });
}

export function cacheClear() {
  const n = CACHE.size;
  CACHE.clear();
  return n;
}

/**
 * cachedGet(url, options) — drop-in for `axios.get` that consults the cache
 * first. Returns a Promise that resolves to `{ data }` so calling code
 * doesn't need to branch on cached-vs-fresh.
 *
 * The `nocache` option bypasses the cache (e.g. when the user clicks
 * Refresh). The `ttl` option overrides the default 5min.
 */
export async function cachedGet(url, { params, nocache } = {}) {
  if (!nocache) {
    const hit = cacheGet(url, params);
    if (hit !== null) return { data: hit, _cached: true };
  }
  const resp = await axios.get(url, { params });
  cacheSet(url, params, resp.data);
  return { data: resp.data, _cached: false };
}
